import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import UndoIcon from '@mui/icons-material/Undo'
import { WorkstationDownTimeRowType } from './Scheduler.types'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HOURS = 168

/** Parse "(0,8),(24,32)" → [[0,8],[24,32]] */
const parseDowntimeStr = (s: string): [number, number][] => {
  if (!s || s === 'null' || s === 'None') return []
  const intervals: [number, number][] = []
  const re = /\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*\)/g
  let m: RegExpExecArray | null
  while ((m = re.exec(s)) !== null) {
    intervals.push([Math.round(parseFloat(m[1])), Math.round(parseFloat(m[2]))])
  }
  return intervals
}

/** Convert boolean[168] → interval string "(0,8),(24,32)" */
const toDowntimeStr = (grid: boolean[]): string => {
  const intervals: [number, number][] = []
  let i = 0
  while (i < HOURS) {
    if (grid[i]) {
      const start = i
      while (i < HOURS && grid[i]) i++
      intervals.push([start, i])
    } else {
      i++
    }
  }
  if (intervals.length === 0) return ''
  return intervals.map(([a, b]) => `(${a},${b})`).join(',')
}

/** Intervals → boolean[168] */
const intervalsToGrid = (intervals: [number, number][]): boolean[] => {
  const grid = new Array<boolean>(HOURS).fill(false)
  for (const [start, end] of intervals) {
    for (let i = Math.max(0, start); i < Math.min(HOURS, end); i++) {
      grid[i] = true
    }
  }
  return grid
}

// ─── Component ───────────────────────────────────────────────────────────────

type DowntimeGanttProps = {
  downtimeRows: WorkstationDownTimeRowType[]
  resourceName: string
  onSave: (updatedRow: Record<number, Record<string, unknown>>) => void
}

export const DowntimeGantt = ({
  downtimeRows,
  resourceName,
  onSave,
}: DowntimeGanttProps) => {
  const row = downtimeRows.find((r) => r.resource === resourceName)
  const originalStr = row?.downTime || ''

  const [grid, setGrid] = useState<boolean[]>(() =>
    intervalsToGrid(parseDowntimeStr(originalStr))
  )
  const [hasChanges, setHasChanges] = useState(false)

  // Reset when resource changes
  useEffect(() => {
    const r = downtimeRows.find((r) => r.resource === resourceName)
    const str = r?.downTime || ''
    setGrid(intervalsToGrid(parseDowntimeStr(str)))
    setHasChanges(false)
  }, [resourceName, downtimeRows])

  // Drag state
  const dragging = useRef(false)
  const dragMode = useRef<boolean>(false) // true = painting downtime, false = erasing
  const dragStartCell = useRef<number>(-1)

  const handleMouseDown = useCallback(
    (hour: number) => {
      dragging.current = true
      dragStartCell.current = hour
      // If the cell is currently downtime, we're erasing; otherwise painting
      dragMode.current = !grid[hour]
      setGrid((prev) => {
        const next = [...prev]
        next[hour] = dragMode.current
        return next
      })
      setHasChanges(true)
    },
    [grid]
  )

  const handleMouseEnter = useCallback((hour: number) => {
    if (!dragging.current) return
    setGrid((prev) => {
      const next = [...prev]
      next[hour] = dragMode.current
      return next
    })
  }, [])

  const handleMouseUp = useCallback(() => {
    dragging.current = false
  }, [])

  // Attach global mouseup to handle drag ending outside the grid
  useEffect(() => {
    const handler = () => {
      dragging.current = false
    }
    window.addEventListener('mouseup', handler)
    return () => window.removeEventListener('mouseup', handler)
  }, [])

  const handleSave = useCallback(() => {
    const newStr = toDowntimeStr(grid)
    if (row) {
      onSave({ [row.id]: { ...row, downTime: newStr } })
    }
    setHasChanges(false)
  }, [grid, row, onSave])

  const handleReset = useCallback(() => {
    setGrid(intervalsToGrid(parseDowntimeStr(originalStr)))
    setHasChanges(false)
  }, [originalStr])

  // Summary stats
  const totalDowntime = useMemo(() => grid.filter(Boolean).length, [grid])
  const totalAvailable = HOURS - totalDowntime

  if (!row) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary" fontSize="0.85rem">
          No downtime data for {resourceName}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ userSelect: 'none' }} onMouseUp={handleMouseUp}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography fontSize="0.8rem" color="text.secondary">
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 10,
                height: 10,
                bgcolor: '#ef5350',
                borderRadius: 0.5,
                mr: 0.5,
                verticalAlign: 'middle',
              }}
            />
            Downtime: <strong>{totalDowntime}h</strong>
          </Typography>
          <Typography fontSize="0.8rem" color="text.secondary">
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 10,
                height: 10,
                bgcolor: '#e8f5e9',
                border: '1px solid #c8e6c9',
                borderRadius: 0.5,
                mr: 0.5,
                verticalAlign: 'middle',
              }}
            />
            Available: <strong>{totalAvailable}h</strong>
          </Typography>
        </Box>
        {hasChanges && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Undo changes">
              <IconButton size="small" onClick={handleReset}>
                <UndoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Save
            </Button>
          </Box>
        )}
      </Box>

      {/* Grid */}
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '40px repeat(24, 1fr)',
            gap: '1px',
            minWidth: 600,
          }}
        >
          {/* Hour header row */}
          <Box />
          {Array.from({ length: 24 }, (_, h) => (
            <Box
              key={`hdr-${h}`}
              sx={{
                textAlign: 'center',
                fontSize: '0.55rem',
                color: 'text.secondary',
                fontWeight: 600,
                py: 0.25,
              }}
            >
              {h.toString().padStart(2, '0')}
            </Box>
          ))}

          {/* Day rows */}
          {DAY_LABELS.map((day, dayIdx) => (
            <>
              <Box
                key={`label-${day}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  pr: 1,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                }}
              >
                {day}
              </Box>
              {Array.from({ length: 24 }, (_, h) => {
                const hour = dayIdx * 24 + h
                const isDown = grid[hour]
                return (
                  <Tooltip
                    key={`cell-${hour}`}
                    title={`${day} ${h}:00–${h + 1}:00 — ${isDown ? 'Downtime' : 'Available'}`}
                    arrow
                    placement="top"
                  >
                    <Box
                      onMouseDown={() => handleMouseDown(hour)}
                      onMouseEnter={() => handleMouseEnter(hour)}
                      sx={{
                        height: 22,
                        bgcolor: isDown ? '#ef5350' : '#e8f5e9',
                        border: '1px solid',
                        borderColor: isDown ? '#c62828' : '#c8e6c9',
                        borderRadius: 0.3,
                        cursor: 'pointer',
                        transition: 'background-color 0.1s',
                        '&:hover': {
                          bgcolor: isDown ? '#e53935' : '#c8e6c9',
                          boxShadow: '0 0 0 1px #1976d2',
                        },
                      }}
                    />
                  </Tooltip>
                )
              })}
            </>
          ))}
        </Box>
      </Box>

      {/* Hint */}
      <Typography
        fontSize="0.65rem"
        color="text.secondary"
        sx={{ mt: 0.5, fontStyle: 'italic' }}
      >
        Click and drag to toggle downtime. Red = downtime, green = available.
      </Typography>
    </Box>
  )
}
